import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { AccountRole, LoginResponseDto } from '@ivy-monorepo/shared'
import { authService } from '../services/auth.service'
import { LocalStorageKey } from '../helpers/constants'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: AccountRole
}

const ProtectedRoute = ({ children, requiredRole }: ProtectedRouteProps) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const location = useLocation()

  useEffect(() => {
    const checkAuthorization = async () => {
      // First check if user is authenticated
      if (!authService.isAuthenticated()) {
        setIsAuthenticated(false)
        setIsAuthorized(false)
        return
      }

      // User is authenticated
      setIsAuthenticated(true)

      // For paths that don't require specific roles
      if (!requiredRole) {
        setIsAuthorized(true)
        return
      }

      // Try to get account from localStorage first
      const authDataFromStorage = localStorage.getItem(LocalStorageKey.auth)
      if (authDataFromStorage) {
        try {
          const parsedAuthDataFromStorage = JSON.parse(authDataFromStorage) as LoginResponseDto
          if (parsedAuthDataFromStorage.account.role === requiredRole) {
            setIsAuthorized(true)
            return
          }
        } catch (e) {
          // Invalid JSON, will fetch data from API
        }
      }

      // If no cached data or invalid role, fetch from API
      try {
        const account = await authService.getCurrentAccount()
        if (account && account.role === requiredRole) {
          setIsAuthorized(true)
        } else {
          setError(`This area requires elevated privileges`)
          setTimeout(() => setIsAuthorized(false), 1000)
        }
      } catch (err) {
        console.error('Authorization check failed:', err)
        setError('Failed to verify authorization')
        setIsAuthorized(false)
      }
    }

    checkAuthorization()
  }, [requiredRole, location.pathname])

  // Show loading state while checking authorization
  if (isAuthorized === null) {
    return <></>
  }

  // Redirect to login if not authenticated or unauthorized
  if (!isAuthorized) {
    // Different message based on authentication status
    const redirectMessage = isAuthenticated
      ? error || 'You do not have permission to access this page'
      : 'Please log in to continue'

    return !isAuthenticated ? (
      <Navigate
        to={`/login?error=${encodeURIComponent(redirectMessage)}&redirectTo=${encodeURIComponent(location.pathname)}`}
        replace
      />
    ) : (
      <Navigate to="/login" replace />
    )
  }

  // If authorized, render the children
  return <>{children}</>
}

export default ProtectedRoute
